import {EmailDto} from "./email.dto";
import {SettingDto} from "./setting.dto";
import {PermissionScope} from "../../services/auth/permission-scope.enum";
import {PermissionGrant} from "../../services/auth/permission-grant.enum";
import {CurrencyDto} from "./currency.dto";
import {VatRateDto} from "./vat-rate.dto";
import {AddressDto} from "./address.dto";
import {BankAddressDto} from "./bank-address.dto";
import {PhoneDto} from "./phone.dto";
import {OrgFeatureDto} from "./org-feature.dto";
import {OrgLifecycleDto} from "./org-lifecycle.dto";
import {Feature} from "../../libs/enum/feature.enum";
import {isDefined, isTrue} from "../../libs/utils/epic.utils";

export class OrgDto {

  public id!: string;
  public name: string = '';
  public taxNumber: string | undefined;
  public countryCode!: string;
  public languageCode!: string;
  public timezone!: string;
  public sector: string | undefined;
  public defaultCurrency!: CurrencyDto;
  public defaultVatRate!: VatRateDto;
  public website: string | undefined;
  public readonly enabled: boolean | undefined;
  public readonly logoId: string | undefined;
  public readonly settings: SettingDto[] = [];
  public readonly permissions: Map<PermissionScope, PermissionGrant> = new Map<PermissionScope, PermissionGrant>();
  public readonly permissionsExists: boolean | undefined; // Optional: is set if needed
  public readonly emails: EmailDto[] = [];
  public readonly phones: PhoneDto[] = [];
  public readonly addresses: AddressDto[] = [];
  public readonly bankAddresses: BankAddressDto[] = [];
  public readonly features: OrgFeatureDto[] = [];
  /** Nur nach einer Kündigung gesetzt — er erklärt, warum Schaltflächen zum Schreiben fehlen. */
  public readonly lifecycle: OrgLifecycleDto | undefined;
  public verificationId: string | undefined;

  constructor(init?: Partial<OrgDto>) {
    Object.assign(this, init);
    if (init?.defaultCurrency) {
      this.defaultCurrency = new CurrencyDto(init.defaultCurrency);
    }
    if (init?.defaultVatRate) {
      this.defaultVatRate = new VatRateDto(init.defaultVatRate);
    }
    if (init?.settings) {
      this.settings = init.settings.map((i) => new SettingDto(i));
    } else {
      this.settings = [];
    }
    if (init?.permissions) {
      if (init.permissions instanceof Map) {
        this.permissions = init.permissions;
      } else {
        this.permissions = new Map<PermissionScope, PermissionGrant>();
        Object.keys(init.permissions).forEach((k: string) => {
          const key: PermissionScope = PermissionScope[k as keyof typeof PermissionScope];
          // @ts-ignore
          const value = PermissionGrant[init.permissions[k] as keyof typeof PermissionGrant];
          this.permissions.set(key, value);
        });
      }
      this.permissionsExists = this.permissions.size > 0;
    } else {
      this.permissions = new Map();
      // Do not set 'permissionsExists', there are cases where 'permissions' are not passed even if they exist
    }
    if (init?.emails) {
      this.emails = init.emails.map((i) => new EmailDto(i));
    } else {
      this.emails = [];
    }
    if (init?.phones) {
      this.phones = init.phones.map((i) => new PhoneDto(i));
    } else {
      this.phones = [];
    }
    if (init?.addresses) {
      this.addresses = init.addresses.map((i) => new AddressDto(i));
    } else {
      this.addresses = [];
    }
    if (init?.bankAddresses) {
      this.bankAddresses = init.bankAddresses.map((i) => new BankAddressDto(i));
    } else {
      this.bankAddresses = [];
    }
    if (init?.lifecycle) {
      this.lifecycle = new OrgLifecycleDto(init.lifecycle);
    }
    if (init?.features) {
      this.features = init.features.map((i) => new OrgFeatureDto(i));
    } else {
      this.features = [];
    }
  }

  public isEnabled(): boolean {
    return isTrue(this.enabled);
  }

  public isFeatureEnabled(feature: Feature): boolean {
    // FUNERAL_EXTENDED automatically activates CONTACT_RELATION (TRIAL_CONTACT_RELATION)
    if ((feature === Feature.CONTACT_RELATION || feature === Feature.TRIAL_CONTACT_RELATION)
      && this.isFeatureEnabled(Feature.FUNERAL_EXTENDED)) {
      return true;
    }
    return this.isFeatureOrTrialActive(feature);
  }

  public hasPermissions(): boolean {
    return isTrue(this.permissionsExists) || this.permissions.size > 0;
  }

  private isFeatureOrTrialActive(feature: Feature): boolean {
    if (isDefined(this.features) && this.features.length > 0) {
      if (this.features.some((f) => f.type === feature && f.isActive())) {
        return true;
      }

      const trialKey = `TRIAL_${feature}` as keyof typeof Feature;
      if (isDefined(Feature[trialKey])) {
        const trialFeature: Feature = Feature[trialKey];
        return this.features.some((f) => f.type === trialFeature && f.isActive());
      }
    }
    return false;
  }
}
